import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix per le icone di Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const CATEGORY_LABELS = {
  generic: 'Generico', bottiglie: 'Bottiglie', mozziconi: 'Mozziconi',
  tappi: 'Tappi', vetro: 'Vetro', alluminio: 'Alluminio',
  reti_pesca: 'Reti Pesca', reti_cozze: 'Reti Cozze', legno_verniciato: 'Legno',
  plastica_monouso: 'Plastica', polistirolo: 'Polistirolo', metalli: 'Metalli'
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const LeafletMap = ({ 
  collections = [], 
  sosReports = [], 
  wasteReports = [], 
  underwaterReports = [],
  places = [],
  naturalSites = [],
  onMarkerClick,
  center = [42.5, 12.5],
  zoom = 6
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current) return;

    // Inizializza mappa solo una volta
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: true
      }).setView(center, zoom);
      
      // Tile layer OpenStreetMap con stile scuro
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap, © CARTO',
        maxZoom: 19
      }).addTo(mapInstanceRef.current);
    }

    const map = mapInstanceRef.current;

    // Rimuovi marker esistenti
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    const bounds = [];

    // Helper per creare popup HTML
    const createCollectionPopup = (item) => {
      const materials = item.categories ? Object.entries(item.categories)
        .filter(([k, v]) => v && parseFloat(v) > 0)
        .map(([k, v]) => `<span style="background:#0ea5e9;color:white;padding:2px 6px;border-radius:4px;font-size:10px;margin:2px;">${CATEGORY_LABELS[k] || k}: ${v}kg</span>`)
        .join('') : '';
      
      return `
        <div style="min-width:200px;max-width:280px;">
          <div style="font-weight:bold;color:#0891b2;font-size:14px;margin-bottom:8px;">
            ${item.is_sea_collection ? '🚢 Fishing for Litter' : '♻️ Raccolta'}
          </div>
          ${item.photo_url ? `<img src="${item.photo_url}" style="width:100%;max-height:120px;object-fit:cover;border-radius:8px;margin-bottom:8px;" onerror="this.style.display='none'"/>` : ''}
          <div style="font-size:20px;font-weight:bold;color:#06b6d4;margin-bottom:4px;">${item.kg_collected || 0} kg</div>
          <div style="font-size:11px;color:#6b7280;margin-bottom:6px;">
            📅 ${formatDate(item.timestamp || item.created_at)}
          </div>
          ${item.volunteer_name ? `<div style="font-size:12px;color:#374151;margin-bottom:4px;">👤 ${item.volunteer_name}</div>` : ''}
          ${item.place ? `<div style="font-size:12px;color:#10b981;margin-bottom:6px;">📍 ${item.place}</div>` : ''}
          ${materials ? `<div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:2px;">${materials}</div>` : ''}
        </div>
      `;
    };

    const createSOSPopup = (item) => `
      <div style="min-width:200px;max-width:280px;">
        <div style="font-weight:bold;color:#ef4444;font-size:14px;margin-bottom:8px;">
          ⚠️ SOS: ${item.category || 'Emergenza'}
        </div>
        ${item.photo_url ? `<img src="${item.photo_url}" style="width:100%;max-height:120px;object-fit:cover;border-radius:8px;margin-bottom:8px;" onerror="this.style.display='none'"/>` : ''}
        <div style="font-size:11px;color:#6b7280;margin-bottom:6px;">
          📅 ${formatDate(item.timestamp || item.created_at)}
        </div>
        ${item.description ? `<div style="font-size:12px;color:#374151;margin-bottom:4px;">${item.description}</div>` : ''}
        ${item.status ? `<div style="font-size:11px;padding:2px 8px;background:${item.status === 'resolved' ? '#10b981' : '#f59e0b'};color:white;border-radius:4px;display:inline-block;">${item.status === 'resolved' ? 'Risolto' : 'In attesa'}</div>` : ''}
      </div>
    `;

    const createWastePopup = (item) => `
      <div style="min-width:200px;max-width:280px;">
        <div style="font-weight:bold;color:#f59e0b;font-size:14px;margin-bottom:8px;">
          🗑️ Segnalazione Rifiuti
        </div>
        ${item.photo_url ? `<img src="${item.photo_url}" style="width:100%;max-height:120px;object-fit:cover;border-radius:8px;margin-bottom:8px;" onerror="this.style.display='none'"/>` : ''}
        <div style="font-size:11px;color:#6b7280;margin-bottom:6px;">
          📅 ${formatDate(item.timestamp || item.created_at)}
        </div>
        ${item.description ? `<div style="font-size:12px;color:#374151;margin-bottom:4px;">${item.description}</div>` : ''}
        <div style="font-size:11px;padding:2px 8px;background:${item.status === 'resolved' ? '#10b981' : item.status === 'in_progress' ? '#f97316' : '#fbbf24'};color:white;border-radius:4px;display:inline-block;">
          ${item.status === 'resolved' ? '✅ Risolto' : item.status === 'in_progress' ? '🔄 In corso' : '⏳ In attesa'}
        </div>
      </div>
    `;

    const createPlacePopup = (item) => `
      <div style="min-width:200px;max-width:280px;">
        <div style="font-weight:bold;color:#8b5cf6;font-size:14px;margin-bottom:8px;">
          ⭐ ${item.name || 'Luogo'}
        </div>
        ${item.photo_url ? `<img src="${item.photo_url}" style="width:100%;max-height:120px;object-fit:cover;border-radius:8px;margin-bottom:8px;" onerror="this.style.display='none'"/>` : ''}
        ${item.description ? `<div style="font-size:12px;color:#374151;margin-bottom:4px;">${item.description}</div>` : ''}
        ${item.address ? `<div style="font-size:11px;color:#6b7280;">📍 ${item.address}</div>` : ''}
      </div>
    `;

    const createSitePopup = (item) => `
      <div style="min-width:200px;max-width:280px;">
        <div style="font-weight:bold;color:#0ea5e9;font-size:14px;margin-bottom:8px;">
          ${item.site_type === 'amp' ? '🌊 Area Marina Protetta' : item.site_type === 'centro_recupero' ? '🐢 Centro Recupero' : '🔍 Oasi Faunistica'}
        </div>
        <div style="font-size:14px;font-weight:600;color:#1f2937;margin-bottom:4px;">${item.name || 'Sito'}</div>
        ${item.description ? `<div style="font-size:12px;color:#374151;margin-bottom:4px;">${item.description}</div>` : ''}
        ${item.website ? `<a href="${item.website}" target="_blank" style="font-size:11px;color:#0891b2;">🔗 Sito web</a>` : ''}
      </div>
    `;

    // Icone personalizzate
    const createIcon = (color, emoji, size = 28) => L.divIcon({
      className: 'custom-marker',
      html: `<div style="width:${size}px;height:${size}px;background:${color};border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:${size/2}px;">${emoji}</div>`,
      iconSize: [size, size],
      iconAnchor: [size/2, size/2],
      popupAnchor: [0, -size/2]
    });

    const collectionIcon = createIcon('#10B981', '♻️');
    const seaCollectionIcon = createIcon('#F97316', '🚢');
    const sosIcon = createIcon('#EF4444', '⚠️', 32);
    const placeIcon = createIcon('#8B5CF6', '⭐');

    const wasteIcon = (status) => createIcon(
      status === 'resolved' ? '#10B981' : status === 'in_progress' ? '#F97316' : '#FBBF24',
      '🗑️'
    );

    const siteIcon = (type) => createIcon(
      type === 'amp' ? '#0EA5E9' : type === 'centro_recupero' ? '#10B981' : '#06B6D4',
      type === 'amp' ? '🌊' : type === 'centro_recupero' ? '🐢' : '🔍'
    );

    // Collections
    collections.forEach((item, idx) => {
      if (item.location?.lat && item.location?.lng) {
        const pos = [item.location.lat, item.location.lng];
        bounds.push(pos);
        const marker = L.marker(pos, { icon: item.is_sea_collection ? seaCollectionIcon : collectionIcon })
          .addTo(map)
          .bindPopup(createCollectionPopup(item), { maxWidth: 300 });
        markersRef.current.push(marker);
      }
    });

    // SOS Reports
    sosReports.forEach((item, idx) => {
      if (item.location?.lat && item.location?.lng) {
        const pos = [item.location.lat, item.location.lng];
        bounds.push(pos);
        const marker = L.marker(pos, { icon: sosIcon })
          .addTo(map)
          .bindPopup(createSOSPopup(item), { maxWidth: 300 });
        markersRef.current.push(marker);
      }
    });

    // Waste Reports
    wasteReports?.forEach((item, idx) => {
      if (item.location?.lat && item.location?.lng) {
        const pos = [item.location.lat, item.location.lng];
        bounds.push(pos);
        const marker = L.marker(pos, { icon: wasteIcon(item.status) })
          .addTo(map)
          .bindPopup(createWastePopup(item), { maxWidth: 300 });
        markersRef.current.push(marker);
      }
    });

    // Underwater Reports
    underwaterReports?.forEach((item, idx) => {
      if (item.location?.lat && item.location?.lng) {
        const pos = [item.location.lat, item.location.lng];
        bounds.push(pos);
        const marker = L.marker(pos, { icon: createIcon('#06B6D4', '🤿') })
          .addTo(map)
          .bindPopup(`
            <div style="min-width:180px;">
              <div style="font-weight:bold;color:#06b6d4;font-size:14px;margin-bottom:8px;">🤿 Segnalazione Subacquea</div>
              ${item.photo_url ? `<img src="${item.photo_url}" style="width:100%;max-height:100px;object-fit:cover;border-radius:8px;margin-bottom:8px;" onerror="this.style.display='none'"/>` : ''}
              ${item.description ? `<div style="font-size:12px;color:#374151;">${item.description}</div>` : ''}
            </div>
          `, { maxWidth: 300 });
        markersRef.current.push(marker);
      }
    });

    // Places
    places?.forEach((item, idx) => {
      if (item.location?.lat && item.location?.lng) {
        const pos = [item.location.lat, item.location.lng];
        bounds.push(pos);
        const marker = L.marker(pos, { icon: placeIcon })
          .addTo(map)
          .bindPopup(createPlacePopup(item), { maxWidth: 300 });
        markersRef.current.push(marker);
      }
    });

    // Natural Sites
    naturalSites?.forEach((item, idx) => {
      if (item.location?.lat && item.location?.lng) {
        const pos = [item.location.lat, item.location.lng];
        bounds.push(pos);
        const marker = L.marker(pos, { icon: siteIcon(item.site_type) })
          .addTo(map)
          .bindPopup(createSitePopup(item), { maxWidth: 300 });
        markersRef.current.push(marker);
      }
    });

    // Fit bounds
    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
    }

  }, [collections, sosReports, wasteReports, underwaterReports, places, naturalSites, center, zoom]);

  // Cleanup mappa on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div 
      ref={mapRef} 
      style={{ width: '100%', height: '100%', minHeight: '400px', borderRadius: '16px' }}
    />
  );
};

export default LeafletMap;
